import React from "react";

interface FeatureRow {
  title: string;
  description: string;
  image: string;
  alt: string;
}

const featureRows: FeatureRow[] = [
  {
    title: "Minimal by design",
    description:
      "No visual overload, no distracting gimmicks. Just a calm, beautifully organized view of the routines that matter most.",
    image: "/images/iPhone 14 Pro Max.png",
    alt: "Refine app — Today view showing a clean, organized habit list",
  },
  {
    title: "Protect the progress you've earned",
    description:
      "Life is not perfect, and your habit tracker should understand that. Streak protection helps you stay motivated through off days without losing momentum.",
    image: "/images/iPhone 14 Pro Max-1.png",
    alt: "Refine app — Streak Protection modal for pausing habits without breaking streaks",
  },
  {
    title: "Turn momentum into a challenge",
    description:
      "Challenges give your routines extra direction, helping you stay engaged, push a little further, and build stronger consistency over time.",
    image: "/images/iPhone 14 Pro Max-4.png",
    alt: "Refine app — Challenge view for setting habit goals and tracking intensity",
  },
  {
    title: "See the bigger picture",
    description:
      "Your profile brings together streaks, milestones, and progress so you can understand how your habits are shaping over time.",
    image: "/images/iPhone 14 Pro Max-2.png",
    alt: "Refine app — Profile view showing streaks, milestones, and overall progress",
  },
  {
    title: "Progress you can understand at a glance",
    description:
      "See your momentum across weekly, monthly, and yearly views in one calm dashboard. Instantly spot your best period, track today's completion, and monitor trends over time without digging through complex charts.",
    image: "/images/iPhone 14 Pro Max-4.png",
    alt: "Refine app — Progress dashboard with weekly, monthly, and yearly overview",
  },
];

const Features: React.FC = () => {
  return (
    <div>
      {/* Hero */}
      <div
        style={{
          padding: "clamp(32px, 5vw, 56px) clamp(24px, 5vw, 40px) clamp(28px, 4vw, 48px)",
        }}
      >
        <p
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase" as const,
            color: "#2A3D4A",
            margin: "0 0 16px",
          }}
        >
          iOS · React Native
        </p>
        <h1
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: "clamp(2.5rem, 7vw, 3.5rem)",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.08,
            color: "#2A3D4A",
            margin: "0 0 18px",
          }}
        >
          Refine.
        </h1>
        <p
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: "17px",
            fontWeight: 300,
            lineHeight: 1.65,
            color: "#2A3D4A",
            maxWidth: "520px",
            margin: "0 0 36px",
            letterSpacing: "0.01em",
          }}
        >
          Track your routines, protect your momentum, and see meaningful
          progress in a habit app designed for clarity instead of clutter.
        </p>

        {/* App Store Button */}
        <button
          onClick={() =>
            window.open(
              "https://apps.apple.com/us/app/stronghabit-daily-habits/id6743635589",
              "_blank",
              "noopener,noreferrer"
            )
          }
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            background: "#2A3D4A",
            border: "none",
            borderRadius: "22px",
            padding: "10px 20px",
            cursor: "pointer",
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
          </svg>
          <div style={{ textAlign: "left" }}>
            <div
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: "10px",
                fontWeight: 300,
                color: "rgba(255,255,255,0.72)",
                lineHeight: 1.2,
                letterSpacing: "0.04em",
              }}
            >
              Download on the
            </div>
            <div
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: "16px",
                fontWeight: 700,
                color: "#FFFFFF",
                lineHeight: 1.2,
              }}
            >
              App Store
            </div>
          </div>
        </button>
      </div>

      {/* Feature Rows */}
      {featureRows.map((feature, i) => (
        <div
          key={feature.title}
          className={`flex flex-col ${
            i % 2 === 0 ? "sm:flex-row" : "sm:flex-row-reverse"
          } sm:items-center`}
          style={{
            gap: "32px",
            padding: "clamp(32px, 5vw, 48px) clamp(24px, 5vw, 40px)",
            borderBottom:
              i < featureRows.length - 1
                ? "1px solid rgba(42,61,74,0.12)"
                : "none",
          }}
        >
          {/* Mockup image */}
          <div
            className="mx-auto sm:mx-0"
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={feature.image}
              alt={feature.alt}
              style={{
                width: "100%",
                maxWidth: "340px",
                height: "auto",
                objectFit: "contain",
                display: "block",
              }}
              loading="lazy"
            />
          </div>

          {/* Text */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.10em",
                textTransform: "uppercase" as const,
                color: "#2A3D4A",
                margin: "0 0 10px",
              }}
            >
              {`0${i + 1}`}
            </p>
            <h2
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: "clamp(1.375rem, 3vw, 1.875rem)",
                fontWeight: 700,
                letterSpacing: "-0.015em",
                lineHeight: 1.2,
                color: "#2A3D4A",
                margin: "0 0 14px",
              }}
            >
              {feature.title}
            </h2>
            <p
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: "16px",
                fontWeight: 300,
                lineHeight: 1.65,
                color: "#2A3D4A",
                letterSpacing: "0.01em",
                margin: 0,
              }}
            >
              {feature.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Features;
